'use client';
import React, { useState, useEffect } from 'react';
import { ShippingMethod as Shared } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { convertToLocale } from '@lib/util/money';
import { CART_UPDATED_EVENT } from '@/lib/hooks/useCart';
import { checkoutSelections, stepStatus } from './checkout-state';

const SharedRender = (Shared as any).render;

// Module-level state: persists the staging shipping method AND the
// last successfully-saved id across re-mounts of the wrapper. The
// Puck content tree can re-mount on refetch/context changes; without
// this, the user's click would be visible for one render and then
// reset to the (stale) server value on the next mount. The Puck
// context is SSR-frozen, so we also need `lastSavedId` to know what
// the cart has been updated to — without it, `cart.shipping_methods`
// is empty for a window after the save and the "Continue to Payment"
// button stays visible.
const shippingState: { stagingId: string; isSaving: boolean; lastSavedId: string } = {
  stagingId: '',
  isSaving: false,
  lastSavedId: '',
};

/**
 * Storefront ShippingMethod — reads available shipping options from
 * Puck context (injected by the checkout page from listCartShippingMethods)
 * and maps them to the shared component's shape.
 *
 * On click: sets the staging `selectedId` (immediate visual feedback),
 * then POSTs to /api/cart/shipping-method so Medusa actually attaches
 * the option. Dispatches CART_UPDATED_EVENT so the cart in Puck context
 * (and any other useCart() instances) refetches with the new state.
 *
 * Medusa v2 shipping option shape: { id, name, price_type, amount, data, provider }.
 * We treat an option as a "pickup" option if its provider has `metadata.kind === 'pickup'`.
 *
 * HOOKS-ORDER NOTE: every `useState` / `useEffect` MUST be called before
 * any conditional return below. The step-gating `if (...) return null`
 * must come AFTER all hook calls. If a hook is added below the gate,
 * React crashes when the gate transitions from "blocked" to "unblocked"
 * (hook count goes from 3 → 5, breaking the Rules of Hooks).
 */
function ShippingMethodImpl(props: any) {
  const { context } = usePuckContext() as any;
  const cart = context?.cart;
  const options: any[] | undefined = context?.availableShippingMethods;

  // === All hooks must be called in the same order on every render. ===

  // Re-render trigger: the Puck context in the browser is the SSR cart
  // and never updates client-side. When ShippingAddress saves the
  // address, it sets `checkoutSelections.address` and dispatches
  // CART_UPDATED_EVENT — but without this listener the wrapper never
  // re-renders, so the step-gating re-evaluation against the fallback
  // never runs. Bumping `trigger` forces a fresh render so the gate
  // sees the new `checkoutSelections.address`.
  const [trigger, setTrigger] = useState(0);
  useEffect(() => {
    const onCartUpdated = () => setTrigger((t) => t + 1);
    window.addEventListener(CART_UPDATED_EVENT, onCartUpdated);
    return () => window.removeEventListener(CART_UPDATED_EVENT, onCartUpdated);
  }, []);

  // Staging state — what the user has clicked. Module-level so the
  // visual selection survives any re-mount (Puck content tree re-mounts
  // can happen on cart refetch / context changes; without module state
  // the click would visually "stick" for one render then deselect).
  // Initialised from module-level state only (NOT from `serverSelectedId`,
  // which is computed below the gate).
  const [stagingId, setStagingIdLocal] = useState<string>(shippingState.stagingId);
  const setStagingId = (id: string) => {
    shippingState.stagingId = id;
    setStagingIdLocal(id);
  };
  const [isSaving, setIsSavingLocal] = useState<boolean>(shippingState.isSaving);
  const setIsSaving = (v: boolean) => {
    shippingState.isSaving = v;
    setIsSavingLocal(v);
  };

  // === Conditional returns ONLY come after all hooks above. ===

  // Step 2 of the progressive step-by-step checkout: only render once
  // the address is saved. The cart from Puck context may be stale
  // (the page hasn't re-rendered yet after ShippingAddress's save),
  // so we also check `checkoutSelections.address` as a fallback.
  if (!cart) return null;
  if (!stepStatus.isAddressDone(cart, !!checkoutSelections.address)) {
    return null;
  }
  // `trigger` is read here to ensure React tracks it as a dependency
  // for re-render — without this, the linter would strip the unused
  // read and the re-render on CART_UPDATED_EVENT would not happen.
  void trigger;

  // The cart's currently-attached shipping method (server truth).
  const serverSelectedId: string = cart?.shipping_methods?.[0]?.shipping_option_id ?? '';
  // Effective staging = user's click (state) OR the server's selection
  // (cart) as fallback. Lets the user see the right radio checked
  // whether they just clicked or are returning to the step.
  const effectiveStagingId = stagingId || serverSelectedId;

  if (!options) {
    return <SharedRender {...props} methods={undefined} selectedId={effectiveStagingId} />;
  }

  const currency = cart?.currency_code || 'usd';
  const fmt = (amount: number) =>
    convertToLocale({ amount: Number(amount) || 0, currency_code: currency });

  const toShape = (o: any) => ({
    id: o.id,
    name: o.name ?? o.provider?.id ?? 'Shipping',
    price: o.amount == null ? '—' : fmt(o.amount),
    time: o.data?.eta_days
      ? `${o.data.eta_days} business day${o.data.eta_days === 1 ? '' : 's'}`
      : (o.price_type === 'calculated' ? 'Calculated at checkout' : 'Standard delivery'),
    description:
      o.provider?.metadata?.description ??
      (o.price_type === 'calculated' ? 'Live carrier rates' : 'Flat-rate shipping'),
  });

  // Split options by pickup marker (provider.metadata.kind === 'pickup')
  const pickupSrc = options.find((o: any) => o.provider?.metadata?.kind === 'pickup');
  const deliverySrc = options.filter((o: any) => o.provider?.metadata?.kind !== 'pickup');

  const methods = deliverySrc.map(toShape);
  const pickupOption = pickupSrc ? toShape(pickupSrc) : undefined;

  const handleSelect = (id: string) => {
    if (id === effectiveStagingId || isSaving) return;
    setStagingId(id);
    // Persist the user choice locally so OrderReview (which reads from
    // the stale Puck context) shows the right thing AND so the
    // "Continue to Payment" button can be enabled. The API call is
    // deferred to handleContinue — the user must explicitly click
    // Continue to save the choice and advance.
    const picked = options?.find((o: any) => o.id === id);
    if (picked) {
      checkoutSelections.shippingId = id;
      checkoutSelections.shippingName = picked.name ?? picked.provider?.id ?? 'Shipping';
      checkoutSelections.shippingPrice = picked.amount == null ? '—' : fmt(picked.amount);
      // Raw numeric amount (minor units) so the OrderTotals wrapper
      // can show a live shipping total without parsing the formatted
      // string. Falls back to 0 for "calculated" shipping where the
      // amount isn't known until checkout.
      checkoutSelections.shippingAmount = Number(picked.amount) || 0;
      checkoutSelections.shippingEstimate = picked.data?.eta_days
        ? `${picked.data.eta_days} business day${picked.data.eta_days === 1 ? '' : 's'}`
        : (picked.price_type === 'calculated' ? 'Calculated at checkout' : 'Standard delivery');
    }
  };

  // The "Continue to Payment" button is what actually saves the
  // shipping method to Medusa. Radio clicks just stage the choice in
  // module-level state; this is the explicit "commit" step.
  const handleContinue = async () => {
    if (!stagingId || isSaving) return;
    setIsSaving(true);
    try {
      const res = await fetch('/api/cart/shipping-method', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shippingMethodId: stagingId }),
      });
      if (!res.ok) {
        // Roll back staging on failure
        setStagingId(serverSelectedId);
      } else {
        // Mark this id as the last successfully saved one. We can't
        // rely on `cart.shipping_methods` here because the Puck
        // context is SSR-frozen and won't update until the page
        // re-renders (which won't happen in the same React tree).
        shippingState.lastSavedId = stagingId;
        // Notify useCart() instances to refetch
        window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));
      }
    } catch {
      setStagingId(serverSelectedId);
    } finally {
      setIsSaving(false);
    }
  };

  // Show the "Continue to Payment" button whenever the user has a
  // pending staging that differs from the last successfully saved id.
  // This lets the user change their mind after clicking Continue: pick
  // a different radio → staging updates → button shows again → click
  // to re-save. The `lastSavedId` (from module state) is the source of
  // truth here, NOT the SSR-frozen `cart.shipping_methods`.
  const effectiveLastSaved = shippingState.lastSavedId || serverSelectedId;
  const hasPendingChange = !!stagingId && stagingId !== effectiveLastSaved;
  const showContinueButton = hasPendingChange;

  return (
    <div>
      <SharedRender
        {...props}
        methods={methods}
        pickupOption={pickupOption}
        selectedId={effectiveStagingId}
        onSelect={handleSelect}
        onContinue={handleContinue}
        showContinueButton={showContinueButton}
        isLoading={isSaving}
        loadingText="Saving shipping…"
      />
      {/* Same pattern as ShippingAddress: explicit "Saving…" line
          below the section during the API call. The spinner inside
          the button is easy to miss; this gives the user a clear
          visual signal that the click was registered. */}
      {isSaving && (
        <div className="mt-3 text-sm text-gray-500 px-1">Saving shipping method…</div>
      )}
    </div>
  );
}

export const ShippingMethod: any = {
  ...Shared,
  render: (props: any) => <ShippingMethodImpl {...props} />,
};
export default ShippingMethod;
